import axios from "axios";
import {
  getAccessToken,
  isTokenExpired,
  storeAuthTokens,
} from "./authToken";

const API_ORIGIN = process.env.REACT_APP_API_URL || "https://api.dopletech.com";
const REFRESH_PATH = "/auth/jwt/refresh/";
const CSRF_PATH = "/auth/csrf/";
const EXPLICIT_LOGOUT_KEY = "dople_explicit_logout_v1";

let refreshPromise = null;
let keepAliveStarted = false;

function getCookie(name) {
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split("=").slice(1).join("=")) : null;
}

export function markExplicitLogout() {
  try {
    sessionStorage.setItem(EXPLICIT_LOGOUT_KEY, "1");
  } catch (error) {
    // ignore
  }
}

export function clearExplicitLogout() {
  try {
    sessionStorage.removeItem(EXPLICIT_LOGOUT_KEY);
  } catch (error) {
    // ignore
  }
}

export function wasExplicitLogout() {
  try {
    return sessionStorage.getItem(EXPLICIT_LOGOUT_KEY) === "1";
  } catch (error) {
    return false;
  }
}

export function resetAuthRefreshState() {
  refreshPromise = null;
}

export function shouldRefreshAccessToken({
  minValiditySeconds = 120,
} = {}) {
  const accessToken = getAccessToken();

  if (!accessToken) {
    return true;
  }

  return isTokenExpired(accessToken, minValiditySeconds);
}

function isCsrfRefreshFailure(error) {
  const status = Number(error?.response?.status || 0);
  const data = error?.response?.data || {};
  const detail = `${data.detail || ""} ${data.reason || ""}`.toLowerCase();
  return status === 403 && detail.includes("csrf");
}

async function ensureCsrfCookie() {
  const response = await axios.get(`${API_ORIGIN}${CSRF_PATH}`, {
    withCredentials: true,
  });
  return response?.data?.csrfToken || getCookie("dople_csrf");
}

async function postRefresh(csrfTokenOverride = null) {
  const csrfToken = csrfTokenOverride || getCookie("dople_csrf");
  return axios.post(`${API_ORIGIN}${REFRESH_PATH}`, {}, {
    withCredentials: true,
    headers: csrfToken ? { "X-CSRFToken": csrfToken } : {},
  });
}

export async function refreshAuthTokens() {
  if (wasExplicitLogout()) {
    const error = new Error("Refresh blocked after explicit logout.");
    error.response = { status: 401, data: { detail: "Logged out" } };
    throw error;
  }

  if (!refreshPromise) {
    refreshPromise = postRefresh()
      .catch(async (error) => {
        // Only recover from an actual CSRF rejection. Other 403 responses
        // (for example rate limiting) must not trigger a second refresh POST.
        if (!isCsrfRefreshFailure(error)) {
          throw error;
        }
        const csrfToken = await ensureCsrfCookie();
        return postRefresh(csrfToken);
      })
      .then((response) => {
        const payload = response?.data || {};
        if (!payload.access) {
          throw new Error("Refresh response missing access token.");
        }

        clearExplicitLogout();
        storeAuthTokens({
          access: payload.access,
        });

        return payload;
      })
      .catch((error) => {
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

export function startAuthKeepAlive({
  intervalMs = 10 * 60 * 1000,
  minValiditySeconds = 60 * 60,
} = {}) {
  if (keepAliveStarted || typeof window === "undefined") {
    return;
  }
  keepAliveStarted = true;

  const refreshIfNeeded = () => {
    // Initial session restoration is handled by checkAuthenticated/load_user.
    // Keep-alive should maintain an established access session, not repeatedly
    // probe refresh on anonymous login and signup pages.
    if (wasExplicitLogout()) {
      return;
    }
    if (!getAccessToken()) {
      return;
    }
    if (!shouldRefreshAccessToken({ minValiditySeconds })) {
      return;
    }
    refreshAuthTokens().catch((error) => {
      const status = Number(error?.response?.status || 0);
      if (status === 401) {
        console.warn("[authSession] keep-alive refresh rejected");
      } else {
        console.warn("[authSession] keep-alive refresh deferred");
      }
    });
  };

  window.setInterval(refreshIfNeeded, intervalMs);
  window.addEventListener("focus", refreshIfNeeded);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      refreshIfNeeded();
    }
  });

  window.setTimeout(refreshIfNeeded, 1000);
}
